import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Menu, MenuItem, Paper, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useRecoilState } from "recoil";
import dayjs from "dayjs";
import _ from "underscore";
import ContentBody from "../common/content-body/ContentBody";
import { IRADAncillary, IRaDetailView, IRaDetailViewExtensionInfo, IRaDetailViewSubstitutionInfo } from "../models/models";
import SectionHeader from "../../../util/component/SectionHeader";
import SubstitutionInfo from "./substitution-info/SubstitutionInfo";
import FinancialInfo from "./financial-info/FinancialInfo";
import QuotationInfo from "./quotation-info/QuotationInfo";
import DriversInfo from "./drivers-info/DriversInfo";
import BillingOwnersInfo from "./billing-owners-info/BillingOwnersInfo";
import AncillariesInfo from "./ancillaries-info/AncillariesInfo";
import ExtensionInfo from "./extension-info/ExtensionInfo";
import { RA_DETAIL_INITIAL_VALUE } from "./config";
import { fetchRaDetail } from "./services/http.services";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { raDetailState } from "./state";
import { composeFraction, composePercentage, formatCurrency, getLocaleByLang } from "../utils";
import CustomLoader from "../../../util/component/CustomLoader";
import GeneralInfo from "./general-info/GeneralInfo";
import { fetchLegalEntities, raPreViewDownloadPdf } from "../create-ra/services/http.services";
import { DOWNLOAD_PDF_TYPE } from "../create-ra/preview-and-signing/config";
import AttachmentsInfo from "./attachments-info/AttachmentsInfo";
import { useHasScope } from "../../../config/globalConfig";
import { NetworkScope } from "../types";
import { LOCALES } from "../config";
import RenewalsFromExtension from "./renewals-from-extension/RenewalsFromExtension";
import RaNotes from "./ra-notes/RaNotes";
import { HealthAndSafety } from "@mui/icons-material";
import ExtraInfo from "./extra-info/ExtraInfo"
import CustomerReportTab from "./customer-reports/CustomerReportTab";
import { convertUtcToTzIsoString } from "../../../util/timezoneConversion";

const RaDetail = () => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const { t } = useTranslation(['network', 'translation']);
    const navigate = useNavigate();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const appLanguage = useTranslation();
    const urlParams = new URLSearchParams(window.location.search);

    const [raDetail, setRaDetail] = useRecoilState(raDetailState);

    const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
        return getLocaleByLang(appLanguage.i18n.language);
    }, [appLanguage.i18n.language]);

    useQuery({
        queryKey: ['ntw_legal_entities'],
        queryFn: fetchLegalEntities,
    });

    const [activeTabValue, setActiveTabValue] = useState(0);
    const [canSubmit, setCanSubmit] = useState(false);
    const [actionsDisabledStatus, setActionsDisabledStatus] = useState({
        checkOut: false,
        checkIn: false,
        void: false,
        addClaim: false,
        createSubstitution: false,
        createSubstitutionRecovery: false,
        addExtension: false,
        openStolen: false,
        embezzlement: false,
        openRAHistory: false,
        printRA: false
    });

    const params = useParams(); //params.id

    const raDetailForm = useForm<IRaDetailView>({
        defaultValues: RA_DETAIL_INITIAL_VALUE
    });

    const { append, remove } = useFieldArray<IRaDetailView>({
        name: 'driversInfo.additionalDrivers',
        control: raDetailForm.control,
        rules: {
            maxLength: 2
        }
    });

    const extensionInfo = useFieldArray<IRaDetailView>({
        control: raDetailForm.control,
        name: "extensionInfo"
    });

    const substitutionInfo = useFieldArray<IRaDetailView>({
        control: raDetailForm.control,
        name: "substitutionInfo"
    });

    const bookedAncillariesInfo = useFieldArray<IRaDetailView>({
        control: raDetailForm.control,
        name: "ancillariesInfo.booked"
    });

    const counterSaleAncillariesInfo = useFieldArray<IRaDetailView>({
        control: raDetailForm.control,
        name: "ancillariesInfo.counterSale"
    });

    const firstLastName = useMemo(() => {
        if (!!raDetail?.generalInfo?.raOwnerFirstName && !!raDetail?.generalInfo?.raOwnerLastName) {
            return raDetail.generalInfo.raOwnerFirstName + ' ' + raDetail.generalInfo.raOwnerLastName;
        }

        return '';
    }, [raDetail]);

    const totalAmountTaxIncluded = useMemo(() => {
        if (!raDetail) return;
        return formatCurrency(locale as LOCALES, !!raDetail?.quotationInfo?.totalAmountTaxIncluded ? raDetail.quotationInfo.totalAmountTaxIncluded : null);
    }, [raDetail]);

    const extensionInfoValue = raDetailForm.getValues('extensionInfo');

    const handleChangeActiveTab = (e: React.SyntheticEvent, activeTab: number) => {
        e.stopPropagation();

        setActiveTabValue(activeTab);
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMoreActions = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    }

    const handleMenuOnClose = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    }

    const [isKeyless, isRce] = useMemo(() => {
        return [
            !!raDetail?.generalInfo?.keyless,
            !!raDetail?.rce,
        ]
    }, [raDetail]);

    const handleOpenAction = (event: React.SyntheticEvent, actionName: string) => {
        event.stopPropagation();

        switch (actionName) {
            case "check-out":
                if (!!raDetail) {
                    navigate(`/network/check-out/${raDetail.idRentalAgreement}`)
                }
                break;
            case "check-in":
                if (!!raDetail) {
                    navigate(`/network/check-in/${raDetail.idRentalAgreement}`)
                }
                break;
            case "createSubstitution":
                if (!!raDetail) {
                    navigate(`/booking/request-substitution/${raDetail.idRentalAgreement}`)
                }
                break;
            case "createSubstitutionRecovery":
                if (!!raDetail) {
                    navigate(`/booking/request-substitution/${raDetail.idRentalAgreement}?recoveryMode=1`)
                }
                break;
            case "addExtension":
                if (!!raDetail?.generalInfo?.toBeReconciled) {
                    viewErrorMessage('raDetail.error.toBeRiconciled', snackbarTypes.warning);
                    return;
                }

                if (!!raDetail) {
                    navigate(`/network/extension-management/${raDetail.idRentalAgreement}`)
                }
                break;
            case "addClaim":
                if (!!raDetail) {
                    navigate(`/network/claim-insertion/${raDetail.idRentalAgreement}`)
                }
                break;
            case "openVoid":
                if (!!raDetail) {
                    navigate(`/network/open-void/${raDetail.idRentalAgreement}`)
                }
                break;
            case "paymentReconciliation":
                if (!!raDetail) {
                    navigate(`/network/payment-reconciliation/${raDetail.idRentalAgreement}`)
                }
                break;
            case "edit":
                if (!!raDetail) {
                    navigate(`/network/edit-ra/${raDetail.idRentalAgreement}`)
                }
                break;
        }
        setAnchorEl(null);
    }

    const onSuccessFetchRaDetail = (response: any) => {
        if (!!response) {

            //console.log('Ra detail: ' + JSON.stringify(response));

            var isRaRenewalOngoing: boolean = response.renewalInfo?.[0]?.status == "ONGOING"

            setFormValues(response);

            actionsDisabledStatus.void = response.generalInfo?.raState?.code !== "ONGOING" && response.generalInfo?.raState?.code !== "DRAFT";
            // le subscription al momento non possono essere estese da operatore, in caso di forzatura necessaria, si deve mettere a mano il parametro extendSub=true nell'url
            if (response.financialInfo?.segment === 'SUB') {
                if (urlParams.get("extendSub") !== null) {
                    actionsDisabledStatus.addExtension = ['ONGOING', 'EXPIRED', 'PENDING'].includes(response.generalInfo?.raState?.code);
                } else {
                    actionsDisabledStatus.addExtension = false;
                }
            } else {
                actionsDisabledStatus.addExtension = ['ONGOING', 'EXPIRED', 'PENDING'].includes(response.generalInfo?.raState?.code);
            }
            actionsDisabledStatus.createSubstitution = response.generalInfo?.raState?.code === "ONGOING";
            actionsDisabledStatus.createSubstitutionRecovery = response.generalInfo?.raState?.code === "ONGOING"
                || response.generalInfo?.raState?.code === "EXPIRED"
                || (response.generalInfo?.raState?.code === "CLOSED" && isRaRenewalOngoing);
            setActionsDisabledStatus(actionsDisabledStatus);

            setRaDetail(response);
        }
    }

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const onErrorFetchRaDetail = (error: any) => {
        viewErrorMessage('translation:error.tryLater');
    }

    const setFormValues = (raDetail: any) => {
        if (!!raDetail) {
            // General info
            let pickUpDate = !!raDetail.generalInfo?.effectiveCheckOutDate ? convertUtcToTzIsoString(raDetail.generalInfo.effectiveCheckOutDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : !!raDetail.generalInfo?.actualRentalStart ? convertUtcToTzIsoString(raDetail.generalInfo.actualRentalStart, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null;
            raDetailForm.setValue("generalInfo.raNumber", raDetail.generalInfo.raNumber);
            raDetailForm.setValue("generalInfo.dmsIdentificationCode", raDetail.generalInfo.dmsIdentificationCode);
            raDetailForm.setValue("generalInfo.pickupDate", pickUpDate);
            raDetailForm.setValue("generalInfo.renewalsAndSubstitutions", raDetail.generalInfo.renewalsAndSubstitutions);
            raDetailForm.setValue("generalInfo.raStatus", raDetail.generalInfo.raState);
            raDetailForm.setValue("generalInfo.dmsOperator", raDetail.generalInfo.dmsOperatorIncharge);
            raDetailForm.setValue("generalInfo.reservationNumber", raDetail.generalInfo.reservationNumber);
            raDetailForm.setValue("generalInfo.dossier", raDetail.generalInfo.dossier);
            raDetailForm.setValue("generalInfo.totalRentalDays", raDetail?.generalInfo?.totalBookedRentalDays ?? '');
            raDetailForm.setValue("generalInfo.effectiveRentalDays", raDetail?.generalInfo?.totalRentalDays ?? '');
            raDetailForm.setValue("generalInfo.mileageIncluded", raDetail.generalInfo.mileageIncluded === '0KM' ? t("raDetail.generalInfo.form.label.unlimited") : raDetail.generalInfo.mileageIncluded);
            raDetailForm.setValue("generalInfo.bookedVehicleType", raDetail.generalInfo.bookedVehicleType);
            raDetailForm.setValue("generalInfo.upSell", raDetail.generalInfo.upsell);
            raDetailForm.setValue("generalInfo.bookedAcriss", raDetail.generalInfo.bookedCargroup);
            raDetailForm.setValue("generalInfo.bookedAcrissModel", raDetail.generalInfo.bookedAcrissDescription);
            raDetailForm.setValue("generalInfo.billedCarGroup", raDetail.generalInfo.billingCargroup);
            raDetailForm.setValue("generalInfo.billedCarGroupModel", raDetail.generalInfo.billingAcrissDescription);
            raDetailForm.setValue("generalInfo.rfidCard", raDetail.generalInfo.rfidCard);
            raDetailForm.setValue("generalInfo.eToll", raDetail.vehicle.etollEquipmentNumber);
            raDetailForm.setValue("generalInfo.keyless", raDetail.generalInfo.keyless);
            raDetailForm.setValue("generalInfo.effectiveAcrissId", raDetail.generalInfo.effectiveAcrissId);
            raDetailForm.setValue("generalInfo.effectiveAcrissDescription", raDetail.generalInfo.effectiveAcrissDescription);
            raDetailForm.setValue("generalInfo.plateNumber", raDetail.generalInfo.plateNumber);
            raDetailForm.setValue("generalInfo.vehicleId", raDetail.generalInfo.vehicleId);
            raDetailForm.setValue("generalInfo.toBeReconciled", raDetail.generalInfo.toBeReconciled);
            raDetailForm.setValue("keylessArea.keyLessInfo", raDetail.keylessArea?.keyLessInfo ?? '');

            raDetailForm.setValue("generalInfo.pickupLocation", raDetail.generalInfo.pickupLocation);

            raDetailForm.setValue("generalInfo.expectedPickupDate", !!raDetail.generalInfo?.pickupDate ? convertUtcToTzIsoString(raDetail.generalInfo.pickupDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null);

            // Pickup (check-out)
            let actualRentalStart = null;
            {/*se è una ra di rinnovo e expected_check_out > scan_generation_date allora mostra expected_check_out, altrimenti scan_generation_date*/ }
            // idRentalAgreement: "000326|1183000449|240926|R00S00" effettuare il controllo basandosi sul valore di ROO
            let idRaParts = raDetail.idRentalAgreement.split('|');
            let renew = !idRaParts[idRaParts.length - 1].includes('R00');
            if (renew && !!raDetail.generalInfo.pickupDate && !!raDetail.generalInfo.effectiveCheckOutDate) {
                actualRentalStart = raDetail.generalInfo.pickupDate > raDetail.generalInfo.effectiveCheckOutDate ?
                    convertUtcToTzIsoString(raDetail.generalInfo.pickupDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) :
                    convertUtcToTzIsoString(raDetail.generalInfo.effectiveCheckOutDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId);
            } else {
                actualRentalStart = !!raDetail.generalInfo?.effectiveCheckOutDate ? convertUtcToTzIsoString(raDetail.generalInfo.effectiveCheckOutDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : !!raDetail.generalInfo?.actualRentalStart ? convertUtcToTzIsoString(raDetail.generalInfo.actualRentalStart, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null;
            }
            raDetailForm.setValue("generalInfo.actualRentalStart", actualRentalStart);

            raDetailForm.setValue("generalInfo.kmOnStart", raDetail.generalInfo.kmOnstart);
            raDetailForm.setValue("generalInfo.tankLevelOnStart", composeFraction(raDetail.generalInfo.tanklevelOnstart));
            raDetailForm.setValue("generalInfo.batteryOnStart", composePercentage(raDetail.generalInfo.batteryOnstart));

            // Check-out
            raDetailForm.setValue("generalInfo.kmsOnCheck", raDetail.generalInfo.kmOncheck);
            raDetailForm.setValue("generalInfo.tankLevelOnCheck", composeFraction(raDetail.generalInfo.tanklevelOncheck));
            raDetailForm.setValue("generalInfo.batteryOnCheck", composePercentage(raDetail.generalInfo.batteryOncheck));

            //Drop-off
            raDetailForm.setValue("generalInfo.dropoffLocation", raDetail.generalInfo.dropoffLocation);
            raDetailForm.setValue("generalInfo.expectedDropoffDate", !!raDetail.generalInfo?.expectedDropoffDate ? convertUtcToTzIsoString(raDetail.generalInfo.expectedDropoffDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null);
            raDetailForm.setValue("generalInfo.actualDropoffDate", !!raDetail.generalInfo?.actualDropoffDate ? convertUtcToTzIsoString(raDetail.generalInfo.actualDropoffDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null);
            raDetailForm.setValue("generalInfo.dropOffKmOnCheck", raDetail.generalInfo.dropoffKmOncheck);
            raDetailForm.setValue("generalInfo.dropOffTankLevelOnCheck", composeFraction(raDetail.generalInfo.dropoffTanklevelOncheck));
            raDetailForm.setValue("generalInfo.dropOffbatteryOnCheck", composePercentage(raDetail.generalInfo.dropoffBatteryOncheck));

            // substitutionInfo
            if (!!raDetail?.substitutionInfo?.childrenRaList) {
                substitutionInfo.remove();
                raDetail.substitutionInfo.childrenRaList
                    .forEach((substitution: any) => {
                        substitutionInfo.append({
                            idRentalAgreement: !!substitution.idRentalAgreement ? substitution.idRentalAgreement : '',
                            raState: !!substitution.raState?.description ? substitution.raState.description : '',
                            pickupDate: !!substitution.pickupDate ? convertUtcToTzIsoString(substitution.pickupDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null,
                            dropoffDate: !!substitution.dropoffDate ? convertUtcToTzIsoString(substitution.dropoffDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null
                        } as IRaDetailViewSubstitutionInfo)
                    });
            }

            // Financial Info
            raDetailForm.setValue("financialInfo.segment", raDetail.financialInfo.segment);
            raDetailForm.setValue("financialInfo.category", raDetail.financialInfo.category);
            raDetailForm.setValue("financialInfo.product", raDetail.financialInfo.product);
            raDetailForm.setValue("financialInfo.rate", raDetail.financialInfo.rate);
            raDetailForm.setValue("financialInfo.businessAgreement", raDetail.financialInfo.businessAgreement);
            raDetailForm.setValue("financialInfo.creditType", raDetail.financialInfo.creditType);
            raDetailForm.setValue("financialInfo.firstPreauthCardNumber", raDetail.financialInfo.preAuthFirstCreditCardNumber);
            raDetailForm.setValue("financialInfo.secondPreauthCardNumber", raDetail.financialInfo.preAuthSecondCreditCardNumber);

            // Quotation Info
            raDetailForm.setValue("quotationInfo.reservedTariffs", !!raDetail.financialInfo?.reservedTariffs);
            raDetailForm.setValue("quotationInfo.rentalAmount", formatCurrency(locale as LOCALES, raDetail.quotationInfo.rentalAmount));
            raDetailForm.setValue("quotationInfo.bookedAncillariesAmount", formatCurrency(locale as LOCALES, raDetail.quotationInfo.totAncillariesAmountBooked));
            raDetailForm.setValue("quotationInfo.addedAncillariesAmount", formatCurrency(locale as LOCALES, raDetail.quotationInfo.addedAncillariesAmount));

            raDetailForm.setValue("quotationInfo.totalAmountTaxIncluded",
                raDetail.quotationInfo.promoRentalAmountVatIncludedTotal === 0 ?
                    formatCurrency(locale as LOCALES, raDetail.quotationInfo.promoRentalAmountVatIncludedTotal) :
                    formatCurrency(locale as LOCALES, raDetail.quotationInfo.totalAmountTaxIncluded)
            );

            raDetailForm.setValue("quotationInfo.airportTax", formatCurrency(locale as LOCALES, raDetail.quotationInfo.airportTax));
            raDetailForm.setValue("quotationInfo.railwayTax", formatCurrency(locale as LOCALES, raDetail.quotationInfo.railwayTax));
            raDetailForm.setValue("quotationInfo.roadTax", formatCurrency(locale as LOCALES, raDetail.quotationInfo.roadTax));
            raDetailForm.setValue("quotationInfo.totalAmountTaxExcluded", formatCurrency(locale as LOCALES, raDetail.quotationInfo.totalAmountTaxExcluded));
            raDetailForm.setValue("quotationInfo.damageExcess", formatCurrency(locale as LOCALES, raDetail.quotationInfo.damageExcess));
            raDetailForm.setValue("quotationInfo.fireTheftExcess", formatCurrency(locale as LOCALES, raDetail.quotationInfo.fireTheftExcess));

            // driversInfo
            raDetailForm.setValue("driversInfo.mainDriver.firstName", raDetail.driversInfo.mainDriver.firstName);
            raDetailForm.setValue("driversInfo.mainDriver.lastName", raDetail.driversInfo.mainDriver.lastName);
            raDetailForm.setValue("driversInfo.mainDriver.mobile", raDetail.driversInfo.mainDriver.phone);
            raDetailForm.setValue("driversInfo.mainDriver.email", raDetail.driversInfo.mainDriver.email);


            // additionalDrivers
            remove();
            if (!!raDetail.driversInfo.firstDriver.firstName && !!raDetail.driversInfo.firstDriver.lastName) {
                append({
                    firstName: raDetail.driversInfo.firstDriver.firstName,
                    lastName: raDetail.driversInfo.firstDriver.lastName,
                    mobile: raDetail.driversInfo.firstDriver.phone,
                    email: raDetail.driversInfo.firstDriver.email
                });
            }
            if (!!raDetail.driversInfo.driver.firstName && !!raDetail.driversInfo.driver.lastName) {
                append({
                    firstName: raDetail.driversInfo.driver.firstName,
                    lastName: raDetail.driversInfo.driver.lastName,
                    mobile: raDetail.driversInfo.driver.phone,
                    email: raDetail.driversInfo.driver.email
                });
            }

            const billingOwnerTypes = raDetail.billingOwnerInfo.invoiceOwnerSecondary ? ['first', 'second'] : ['first'];

            billingOwnerTypes.forEach((boType) => {
                const customerDetails = boType === 'first' ? raDetail.billingOwnerInfo.invoiceOwner : raDetail.billingOwnerInfo.invoiceOwnerSecondary;
                if (_.isEmpty(customerDetails?.companyName)) { // person
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.customerId`, customerDetails.customerId);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.firstName`, customerDetails.firstName);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.lastName`, customerDetails.lastName);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.fiscalCode`, customerDetails.fiscalCode);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.gender`, customerDetails.gender);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.birthDate`, !!customerDetails?.birthDate ? dayjs(customerDetails.birthDate) : null);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.birthNation`, customerDetails.birthNation);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.birthPlace`, customerDetails.birthPlace);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.countryResidence`, customerDetails.billingCountry);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.provinceResidence`, customerDetails.billingProvince);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.cityResidence`, customerDetails.billingCity);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.address`, customerDetails.billingAddress);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.streetNumber`, customerDetails.billingStreetNumber);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.prefix`, customerDetails.prefix);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.mobile`, customerDetails.mobile);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.firstEmail`, customerDetails.email);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.secondEmail`, customerDetails.secondEmail);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.certifiedEmail`, customerDetails.certifiedEmail);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingCountry`, customerDetails.billingCountry);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingProvince`, customerDetails.billingProvince);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingCity`, customerDetails.billingCity);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingAddress`, customerDetails.billingAddress);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingStreetNumber`, customerDetails.billingStreetNumber);
                } else {// vat or company
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.legalEntity`, customerDetails.legalEntity?.description ? t(`common.legalForms.${customerDetails.legalEntity?.description}`) : '');
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.companyName`, customerDetails.companyName);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.vatNumberCountry`, customerDetails.vatNumberCountry);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.vatNumber`, customerDetails.vatNumber);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.siren`, customerDetails.siren);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.siret`, customerDetails.siret);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.fiscalCode`, customerDetails.fiscalCode);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.firstEmail`, customerDetails.email);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.certifiedEmail`, customerDetails.certifiedEmail);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.SDI`, customerDetails.sdicode);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingCountry`, customerDetails.billingCountry);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingProvince`, customerDetails.billingProvince);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingCity`, customerDetails.billingCity);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingAddress`, customerDetails.billingStreetName);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingStreetNumber`, customerDetails.billingStreetNumber);
                    // @ts-ignore
                    raDetailForm.setValue(`billingOwnerInfo.${boType}.billingPostalCode`, customerDetails.billingPostalCode);
                }
            });

            extensionInfo.remove();
            raDetail.extensionInfo
                .forEach((extension: any) => {
                    const extensionRentalAmount = formatCurrency(locale as LOCALES, extension.extensionRentalAmount);
                    const extensionAncillaryAmount = formatCurrency(locale as LOCALES, extension.extensionAncillaryAmount);
                    const extensionTotalAmount = formatCurrency(locale as LOCALES, extension.extensionTotalAmount);

                    extensionInfo.append({
                        reqExtensionDropoffDateTime: !!extension.requestExtensionDropOffDate ? convertUtcToTzIsoString(extension.requestExtensionDropOffDate, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null,
                        extensionPeriod: extension.extensionPeriod,
                        renewedByExtension: extension.renewedByExtension.code,
                        extendedDropoffDateTimeAllowed: !!extension.expectedDropOffDateAllowed ? convertUtcToTzIsoString(extension.expectedDropOffDateAllowed, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null,
                        lastModifiedBy: extension.lastModifiedBy,
                        extendedTimestamp: !!extension.extendedTimestamp ? convertUtcToTzIsoString(extension.extendedTimestamp, raDetail.generalInfo?.dmsIdentificationCode?.zoneId) : null,
                        billingOwner: extension.billingOwner?.description,
                        rentalExtensionAmount: extensionRentalAmount,
                        ancillariesExtensionAmount: extensionAncillaryAmount,
                        totalExtensionAmount: extensionTotalAmount,
                        reservedPricing: !!raDetail.financialInfo?.reservedTariffs
                    } as IRaDetailViewExtensionInfo)
                });

            // manage Ancillary Info
            bookedAncillariesInfo.remove();
            raDetail?.ancillariesInfo?.ancillaries?.forEach((elem: IRADAncillary, index: number) => {
                bookedAncillariesInfo.append(elem);
                raDetailForm.setValue(`ancillariesInfo.booked.${index}`, elem);
            });

            counterSaleAncillariesInfo.remove();
            raDetail?.ancillariesInfo?.ancillariesAdded?.forEach((elem: any, index: number) => {
                counterSaleAncillariesInfo.append(elem);
                raDetailForm.setValue(`ancillariesInfo.counterSale.${index}`, elem);
            });
        }
    }

    const { isFetching, refetch } = useQuery(['raDetail', { idRentalAgreement: params.id }], () => fetchRaDetail({ idRentalAgreement: params.id }), {
        enabled: false,
        onSuccess: (data) => onSuccessFetchRaDetail(data),
        onError: (error) => onErrorFetchRaDetail(error)
    })

    const downloadCheckoutPdfQuery = useQuery(
        [' raPreViewDownloadPdf', { idRentalAgreement: raDetail?.idRentalAgreement, language: appLanguage.i18n.language, pdfType: DOWNLOAD_PDF_TYPE.CHECKOUT }],
        () => raPreViewDownloadPdf({
            pdfType: DOWNLOAD_PDF_TYPE.CHECKOUT,
            idRentalAgreement: raDetail?.idRentalAgreement,
            language: appLanguage.i18n.language
        }),
        {
            enabled: false,
            onSuccess: (response: any) => {
                if (!!response) {
                    //console.log("checkout** ",response.headers['content-type']);
                    const extension = response.type?.split('/')[1] || 'pdf';
                    console.log("response vvv", extension);
                    const fileURL = URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                     document.body.append(link);
                     link.href = fileURL;
                     link.download = `Check-Out`;
                     link.click();
                     /*
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const pdfURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    document.body.append(link);
                    link.href = pdfURL;
                    link.download = `Check-Out.pdf`;
                    link.click();
                    */
                }
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

    const downloadCheckinPdfQuery = useQuery(
        [' raPreViewDownloadPdf', { idRentalAgreement: raDetail?.idRentalAgreement, language: appLanguage.i18n.language, pdfType: DOWNLOAD_PDF_TYPE.CHECKIN }],
        () => raPreViewDownloadPdf({
            pdfType: DOWNLOAD_PDF_TYPE.CHECKIN,
            idRentalAgreement: raDetail?.idRentalAgreement,
            language: appLanguage.i18n.language
        }),
        {
            enabled: false,
            onSuccess: (response: any) => {
                if (!!response) {
                    //console.log("checkin*** ",response.headers['content-type']);
                    const extension = response.type?.split('/')[1] || 'pdf';
                    console.log("response vvv", extension);
                    const fileURL = URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                     document.body.append(link);
                     link.href = fileURL;
                     link.download = `Check-In`;
                     link.click();
                     /*
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const pdfURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    document.body.append(link);
                    link.href = pdfURL;
                    link.download = `Check-In.pdf`;
                    link.click();
                    */
                }
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

    const downloadRaPdfQuery = useQuery(
        [' raPreViewDownloadPdf', { idRentalAgreement: raDetail?.idRentalAgreement, language: appLanguage.i18n.language, pdfType: DOWNLOAD_PDF_TYPE.RA }],
        () => raPreViewDownloadPdf({
            pdfType: DOWNLOAD_PDF_TYPE.RA,
            idRentalAgreement: raDetail?.idRentalAgreement,
            language: appLanguage.i18n.language
        }),
        {
            enabled: false,
            onSuccess: (response: any) => {
                if (!!response) {
                    //console.log("ra** ",response.headers['content-type']);
                    const extension = response.type?.split('/')[1] || 'pdf';
                    console.log("response vvv", extension);
                    const fileURL = URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                     document.body.append(link);
                     link.href = fileURL;
                     link.download = `RA`;
                     link.click();
                    /*
                    const blob = new Blob([response], { type: 'application/pdf' });
                    const pdfURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    document.body.append(link);
                    link.href = pdfURL;
                    link.download = `RA.pdf`;
                    link.click();
                    */
                }
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

    const handleDownloadCheckout = () => {
        if (!!raDetail) {
            downloadCheckoutPdfQuery.refetch()
        }
    }

    const handleDownloadCheckin = () => {
        if (!!raDetail) {
            downloadCheckinPdfQuery.refetch()
        }
    }

    const handleDownloadRA = () => {
        if (!!raDetail) {
            downloadRaPdfQuery.refetch()
        }
    }

    /** useEffects */
    useEffect(() => {
        if (!raDetail) {
            setCanSubmit(true);
        }
    }, []);

    useEffect(() => {
        if (canSubmit) {
            refetch();
        }
    }, [canSubmit, params.id]);

    return (
        <>
            <SectionHeader title={t('raDetail.header.title')}
                subtitle={
                    <Stack direction="row" alignItems="center" columnGap={3}>
                        <Stack direction="row" alignItems="end" columnGap={1}>
                            <Typography variant="bodyLarge">{t('raDetail.header.subtitle.idRentalAgreement')}</Typography>
                            <Typography variant="bodyLarge">{raDetail?.idRentalAgreement}</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="end" columnGap={1}>
                            <Typography variant="bodyLarge">{t('raDetail.header.subtitle.status')}</Typography>
                            <Typography variant="bodyLarge">{raDetail?.generalInfo?.raState?.description}</Typography>
                        </Stack>

                        <Stack direction="row" alignItems="end" columnGap={1}>
                            <Typography variant="bodyLarge">{t('raDetail.header.subtitle.raOwner')}</Typography>
                            <Typography variant="bodyLarge">
                                {firstLastName}
                            </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="end" columnGap={1}>
                            <Typography variant="bodyLarge">{t('raDetail.header.subtitle.totalAmount')}</Typography>
                            <Typography variant="bodyLarge">
                                {totalAmountTaxIncluded}
                            </Typography>
                        </Stack>

                        {raDetail?.recoveryMode &&
                            <HealthAndSafety
                                titleAccess={t('raDetail.header.subtitle.recoveryLabel')}
                                color="secondary"
                            />
                        }
                    </Stack>
                } />

            <ContentBody>
                <Paper elevation={1} sx={{ p: theme.spacing(2), mb: theme.spacing(2) }}>
                    <Tabs value={activeTabValue} onChange={handleChangeActiveTab} sx={{ mb: theme.spacing(2) }}>
                        <Tab label={t("raDetail.tab.detail")} value={0} />
                        <Tab label={t("raDetail.tab.attachmentsInfo")} value={1} />
                        <Tab label={t("raDetail.tab.notes")} value={2} />
                        <Tab label={t("raDetail.tab.reports")} value={3} />
                    </Tabs>

                    {
                        activeTabValue === 0 &&
                        <Box role={'tabpanel'}>
                            <Stack direction="row" justifyContent="end" alignItems="center" columnGap={2} p={2}>

                                <Button variant={'outlined'} color={"accent"} onClick={handleMoreActions}>
                                    {t('raDetail.button.print')}
                                </Button>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuOnClose}
                                >
                                    {/*<MenuItem onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'openStolen')}*/}
                                    {/*          disabled={!hasScope(NetworkScope.RaInsert) || actionsDisabledStatus.openStolen}>{t("Open Stolen")}</MenuItem>*/}
                                    {/*<MenuItem onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'embezzlement')}*/}
                                    {/*          disabled={!hasScope(NetworkScope.RaInsert) || actionsDisabledStatus.embezzlement}>{t("Open Embezzlement")}</MenuItem>*/}
                                    {/*<Divider sx={{ my: 0.5 }} />*/}
                                    {/*<MenuItem onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'openRAHistory')}*/}
                                    {/*          disabled={actionsDisabledStatus.openRAHistory}>{t("Open RA history")}</MenuItem>*/}

                                    {
                                        hasScope(NetworkScope.RaDetail) &&
                                        <MenuItem onClick={handleDownloadRA}
                                            disabled={actionsDisabledStatus.printRA}>
                                            {t("raDetail.menu.item.printRA")}
                                        </MenuItem>
                                    }
                                    {
                                        hasScope(NetworkScope.CheckOut) &&
                                        <MenuItem onClick={handleDownloadCheckout}
                                            disabled={actionsDisabledStatus.checkOut}>
                                            {t("raDetail.menu.item.printCheckOut")}
                                        </MenuItem>
                                    }

                                    {
                                        hasScope(NetworkScope.CheckIn) &&
                                        <MenuItem onClick={handleDownloadCheckin}
                                            disabled={actionsDisabledStatus.checkIn}>
                                            {t("raDetail.menu.item.printCheckIn")}
                                        </MenuItem>
                                    }
                                </Menu>

                                {
                                    (
                                        isRce || isKeyless
                                    ) &&
                                    [
                                        "Ready-To Pay",
                                        "Ready-Paid",
                                        "Ready-To Sign",
                                        "Ready-Signed",
                                    ].includes(raDetail?.generalInfo?.raState?.label) &&
                                    <>
                                        <Button variant={'contained'} color={"accent"}
                                            onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'edit')}>
                                            {t('raDetail.button.edit')}
                                        </Button>
                                    </>
                                }

                                {
                                    hasScope(NetworkScope.RaInsert) &&
                                    <>
                                        <Button variant={'contained'} color={"accent"}
                                            onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'openVoid')}>
                                            {t('raDetail.button.openVoid')}
                                        </Button>

                                        <Button variant={'contained'} color={"accent"}
                                            onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'addClaim')}>
                                            {t("raDetail.button.addClaim")}
                                        </Button>
                                    </>
                                }

                                {
                                    hasScope(NetworkScope.BookingInsert) &&
                                    actionsDisabledStatus.createSubstitution &&
                                    <Button variant={'contained'} color={"accent"}
                                        disabled={raDetail.hasSubstitution}
                                        onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'createSubstitution')}>
                                        {t("raDetail.button.createSubstitution")}
                                    </Button>
                                }

                                {
                                    hasScope(NetworkScope.BookingSubstitutionInsertRecovery) &&
                                    actionsDisabledStatus.createSubstitutionRecovery &&
                                    <>
                                        <Button variant={'outlined'}
                                            disabled={raDetail.hasSubstitution}
                                            color={"accent"} startIcon={<HealthAndSafety />}
                                            onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'createSubstitutionRecovery')}>
                                            {t('raDetail.button.createSubstitutionRecovery')}
                                        </Button>
                                    </>
                                }

                                {
                                    hasScope(NetworkScope.RaInsert) && actionsDisabledStatus.addExtension &&
                                    <Button variant={'contained'} color={"accent"}
                                        onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'addExtension')}>
                                        {t("raDetail.button.addExtension")}
                                    </Button>
                                }

                                {
                                    hasScope(NetworkScope.RaInsertRecovery) && !!raDetail?.generalInfo?.toBeReconciled &&
                                    <Button variant={'contained'} color={"accent"}
                                        onClick={(e: React.SyntheticEvent) => handleOpenAction(e, 'paymentReconciliation')}>
                                        {t("raDetail.button.paymentReconciliation")}
                                    </Button>
                                }
                            </Stack>

                            <FormProvider {...raDetailForm}>
                                <GeneralInfo />

                                {(!!(raDetail as any)?.substitutionInfo?.childrenRaList && raDetail.substitutionInfo.childrenRaList.length > 0) &&
                                    <SubstitutionInfo />
                                }

                                <FinancialInfo />

                                <QuotationInfo />

                                <DriversInfo />

                                <BillingOwnersInfo />

                                <AncillariesInfo refreshRaDetail={refetch} />

                                {
                                    extensionInfoValue?.map((extension: any, index: number) =>
                                        <ExtensionInfo numExtension={index} key={"extensionI_" + index} />
                                    )
                                }

                                {
                                    raDetail?.renewalInfo?.length > 0 &&
                                    <RenewalsFromExtension />
                                }

                                <ExtraInfo />

                            </FormProvider>
                        </Box>
                    }

                    {/* Attachments Info */}
                    {activeTabValue === 1 &&
                        <Box role={'tabpanel'}>
                            <AttachmentsInfo />
                        </Box>
                    }

                    {/* Notes */}
                    {activeTabValue === 2 &&
                        <Box role={'tabpanel'}>
                            <RaNotes />
                        </Box>
                    }

                    {/* Customer Reports */}
                    {activeTabValue === 3 &&
                        <Box role={'tabpanel'}>
                            <CustomerReportTab raId={raDetail?.idRentalAgreement} raState={raDetail?.generalInfo.raState.code} />
                        </Box>
                    }

                    <CustomLoader loading={isFetching || downloadRaPdfQuery.isFetching || downloadCheckoutPdfQuery.isFetching || downloadCheckinPdfQuery.isFetching} />
                </Paper>
            </ContentBody>
        </>
    );
}

export default RaDetail;